<template>
  <Modal
    name="CREATE_SPECIALTY_MODAL"
    :title="$t('host.label.createSpecialty')"
    actions
  >
    <template v-slot:body>
      <v-form
        v-model="valid"
        class="mw-500 mx-auto"
        ref="createSpecialtyForm"
      >
        <v-row>
          <v-col>
            <v-text-field
              v-model="newSpecialtyName"
              :label="$t('host.label.newSpecialty')"
              outlined
              dense
              autofocus
              :error="!!duplicateError"
              :error-messages="duplicateError"
            />
          </v-col>
        </v-row>
      </v-form>
    </template>

    <template v-slot:actions>
      <v-btn
        @click="closeModal"
        :disabled="isCreating"
        elevation="0"
        class="mr-5"
        text
      >
        {{ $t('appointment.buttons.cancel') }}
      </v-btn>

      <v-btn
        color="primary"
        :loading="isCreating"
        :disabled="!canSave"
        elevation="0"
        @click="save"
      >
        {{ $t('host.label.createSpecialty') }}
      </v-btn>
    </template>
  </Modal>
</template>

<script>
import Modal from '@/components/app/Modal'
import { mapActions, mapMutations, mapGetters } from 'vuex'
import { normalizeTextSearch } from '@/util/helpers'

export default {
  name: 'HostCreateSpecialtyModal',

  components: {
    Modal,
  },

  data() {
    return {
      newSpecialtyName: '',
      isCreating: false,
      valid: false,
    }
  },

  computed: {
    ...mapGetters(['specialties']),

    normalizedSpecialties() {
      return (this.specialties || []).map((s) =>
        this.normalizeName(s.name)
      )
    },

    normalizedNewName() {
      return this.normalizeName(this.newSpecialtyName)
    },

    hasConflict() {
      if (!this.normalizedNewName) return false
      return this.normalizedSpecialties.includes(this.normalizedNewName)
    },

    duplicateError() {
      if (!this.newSpecialtyName) return ''
      if (!this.hasConflict) return ''
      return (
        this.$t('host.message.specialtyAlreadyExists')
      )
    },

    canSave() {
      return (
        this.valid &&
        !!this.normalizedNewName &&
        !this.isCreating &&
        !this.hasConflict
      )
    },
  },

  methods: {
    ...mapActions([
      'createSpecialty',
      'fetchSpecialties',
      'showSuccessMessage',
      'showErrorMessage',
    ]),
    ...mapMutations(['openModal', 'closeAllModals']),

    normalizeName(name) {
      return normalizeTextSearch(name)
        .trim()
        .replace(/[^a-z0-9]/gi, '')
    },

    openModalInstance() {
      this.newSpecialtyName = ''
      this.valid = false
      this.isCreating = false
      this.openModal('CREATE_SPECIALTY_MODAL')
    },

    closeModal() {
      this.closeAllModals()
      this.newSpecialtyName = ''
      this.valid = false
    },

    async save() {
      const name = (this.newSpecialtyName || '').trim()
      if (!name) return

      if (this.hasConflict) {
        this.showErrorMessage(
          this.$t('host.message.specialtyAlreadyExists')
        )
        return
      }

      this.isCreating = true

      try {
        await this.createSpecialty({ name })
        await this.fetchSpecialties({ search: '' })

        this.showSuccessMessage(
          this.$t('host.message.specialtyCreated')
        )

        this.closeModal()
      } catch (error) {
        const message =
          error?.data?.message ||
          this.$t('host.message.specialtyCreateError')
        this.showErrorMessage(message)
      } finally {
        this.isCreating = false
      }
    },
  },
}
</script>
